import React, { useEffect } from "react";

import DatatablePage from "../../../layout/datatable-pages/investors";
import { fetchData, getInvestorsFieldsArray } from "../../../utils/helpers";
import { props } from "../../datatable/props";
import useDatatable from "../../hooks/useDatatable";

import "../datatable-pages.css";
import { useSupabaseApp } from "../../../SupabaseApp";

/**
 * InvestorsLosses Component
 * 
 * Displays a paginated datatable of investors who experienced losses through failed investments.
 * Users can search through 43+ investor attributes including investment history, failed portfolio
 * companies, exit values, investment preferences, and management team. Shows statistics on
 * investors with losses and their failed investment counts. Helps analyze investor track records
 * and risk exposure. Requires user authentication.
 * 
 * @returns {JSX.Element} Datatable page with investors losses data
 */
const InvestorsLosses = () => {
  // Array of field keys for investors datatable
  const fieldKeys = [
    "name",
    "investorType",
    "description",
    "numOfFailedInvestments",
    "lossOnInvestments",
    "shutteredPortfolioCompanies",
    "numOfExit",
    "exitValue",
    "reputation",
    "location",
    "investmentStage",
    "investmentTrackRecord",
    "assets",
    "investmentRange",
    "sweetSpot",
    "investmentFocus",
    "investmentGeography",
    "numOfInvestments",
    "numOfLeadInvestments",
    "numOfFollowOnInvestments",
    "numOfCoInvestments",
    "numOfAcquisitions",
    "boardSeats",
    "numOfFunds",
    "limitedPartners",
    "coInvestors",
    "numofPartners",
    "partners",
    "numOfEmployeesObj",
    "management",
    "boardMembers",
    "founders",
    "email",
    "socialMedia",
    "website",
    "phone",
    "address",
    "entityType",
    "legalName",
    "primaryBusinessName",
    "formerlegalName",
    "founded",
    "stockSymbol",
  ];

  // Convert field keys to formatted field objects for investors
  const fields = getInvestorsFieldsArray(fieldKeys);

  // Get datatable state and props
  const { datatableProps } = useDatatable();

  // Get Supabase utilities and user context
  const { currentUser, supabase } = useSupabaseApp();

  // Verify user authentication
  if (!currentUser) {
    throw new Error(`Must be logged in`);
  }

  // Configuration for investors losses datatable
  const params = {
    supabase,
    ...datatableProps,
    getData: "getInvestorsLosses",
    collection: "investors",
    title: "Investors' losses",
    datatipText:
      "Investigate loss of investments from our collection of failed investments.",
    statsArray: [
      {
        actualName: "investors",
        displayName: "Investors",
        total: "6,185",
      },
      {
        actualName: "failedInvestments",
        displayName: "Failed investments",
        total: "4,585",
      },
    ],
    placeholderText: "Search from investors' losses",
    fields: fields,
    getCompanyDetailsForExpand: "getCompanyDetailsForExpand",
  };

  // Fetch investors data on component mount
  useEffect(() => {
    fetchData(params);
  }, []);

  // Render datatable page with configured params
  return <DatatablePage props={props(params)} />;
};

export default InvestorsLosses;
